"use client";

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { useRouter } from "next/navigation";

type LoginResult = {
  success: boolean;
  error?: string;
};

type AuthContextValue = {
  isAuthenticated: boolean;
  username: string | null;
  login: (username: string, password: string) => LoginResult;
  logout: () => void;
};

const AUTH_PASSWORD = "please-let-me-in";

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState<string | null>(null);

  const login = useCallback(
    (rawUsername: string, password: string): LoginResult => {
      const trimmedUsername = rawUsername.trim();
      if (!trimmedUsername) {
        return { success: false, error: "Username is required." };
      }
      if (password !== AUTH_PASSWORD) {
        return { success: false, error: "Incorrect password." };
      }

      setIsAuthenticated(true);
      setUsername(trimmedUsername);
      router.replace("/new-home");

      return { success: true };
    },
    [router],
  );

  const logout = useCallback(() => {
    setIsAuthenticated(false);
    setUsername(null);
    router.replace("/login");
  }, [router]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      username,
      login,
      logout,
    }),
    [isAuthenticated, username, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
